import jsPDF from "jspdf";
import { LegendElement } from "lib/leaflet/print";
import { IconID } from "types/auth";
import { lightenDarkenColor } from "utils/colorModification";
import { getLigatureFromIconId } from "utils/iconsUtils";

// --- Conversion to print legend --- //
type SetPrintLegendGraficProps = {
  legend: LegendElement;
  doc: jsPDF;
  options: {
    xPos: number;
    yPos: number;
    borderWidth?: number;
  };
};

export const setPrintLegendGrafic = ({ legend, doc, options }: SetPrintLegendGraficProps) => {
  // Unpack input
  const { type, color } = legend;
  const { xPos, yPos, borderWidth } = options;
  const borderWidthValue = borderWidth || 0.28;
  const rectWidth = 3;
  const lineHeight = 1;
  const stripedOffset = borderWidthValue * 6;
  const circleRadius = rectWidth / 4;

  switch (type) {
    // Set internal graphics
    case "FilledRect":
      filledRect(doc, color, xPos, yPos, rectWidth);
      break;
    case "UnfilledRect":
      unfilledRect(doc, xPos, yPos, rectWidth);
      break;
    case "StripesFilledRect":
      stripesFilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth, stripedOffset);
      break;
    case "DashesStripesFilledRect":
      dashesStripesFilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth, stripedOffset);
      break;
    case "DotsFilledRect":
      dotsFilledRect(doc, color, xPos, yPos, rectWidth, circleRadius);
      break;
    case "DotsUnfilled":
      dotsUnfilled(doc, color, xPos, yPos, rectWidth, circleRadius);
      break;
    case "UnfilledRectFilledRect":
      unfilledRectFilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth);
      break;
    case "StripesUnfilledRect":
      stripesUnfilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth, stripedOffset);
      break;
    case "CrossesUnfilledRect":
      crossesUnfilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth, stripedOffset);
      break;
    case "LinesUnfilledRect":
      linesUnfilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth);
      break;
    case "LinesFilledRect":
      linesFilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth);
      break;
    case "VerticalLinesUnfilledRect":
      verticalLinesUnfilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth);
      break;
    case "GridUnfilledRect":
      gridUnfilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth);
      break;
    case "0-9UnfilledDotsUnfilledRect":
      unfilled0_9DotsUnfilledRect(
        doc,
        color,
        xPos,
        yPos,
        borderWidthValue,
        rectWidth,
        circleRadius,
      );
      break;
    case "TriangleUnfilledRect":
      triangleUnfilledRect(doc, color, xPos, yPos, rectWidth);
      break;
    case "TriangleFilledRect":
      triangleFilledRect(doc, color, xPos, yPos, rectWidth);
      break;
    case "RingFilledRect":
      ringFilledRect(doc, color, xPos, yPos, borderWidthValue, rectWidth, circleRadius);
      break;
    case "SquareUnfilledRect":
      squareUnfilledRect(doc, color, xPos, yPos, rectWidth);
      break;
    case "SquareFilledRect":
      squareFilledRect(doc, color, xPos, yPos, rectWidth);
      break;
    case "DiamondFilledRect": 
      diamondFilledRect(doc, color, xPos, yPos, rectWidth);
      break;
    case "StarUnfilledRect":
      startUnfilledRect(doc, color, xPos, yPos, rectWidth);
      break;
    // Lines
    case "Line":
      line(doc, color, xPos, yPos, borderWidthValue, rectWidth, lineHeight);
      break;
    case "DashesLine":
      dashesLine(doc, color, xPos, yPos, borderWidthValue, rectWidth, lineHeight);
      break;
    case "Point":
      point(doc, legend.iconId as IconID, xPos, yPos, borderWidthValue, rectWidth);
      break;
    default:
      return null;
  }
  // Set boarders
  setBorder(doc, type, color, xPos, yPos, borderWidthValue, rectWidth);
};

function point(
  doc: jsPDF,
  iconId: IconID,
  xPos: number,
  yPos: number,
  borderWidthValue: number,
  rectWidth: number,
) {
  const iconImage = getIconImage(iconId);
  if (!iconImage) return null;
  doc.addImage(iconImage, "PNG", xPos, yPos + borderWidthValue, rectWidth, rectWidth); // width and height in mm
}

function getIconImage(iconId: IconID, fontSize = 48): string {
  // Get ligature symbol from iconId
  const ligature = getLigatureFromIconId(iconId);
  if (!ligature) return "";

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  const scale = 2;
  const padding = 0.5;
  canvas.width = fontSize * scale;
  canvas.height = (fontSize + padding) * scale;
  ctx.scale(scale, scale);

  // Load the Material Symbols font
  ctx.font = `${fontSize}px 'Material Icons'`;
  ctx.textBaseline = "middle";
  ctx.fillStyle = "black";

  // Icon text content
  ctx.fillText(ligature, 0, fontSize / 2);

  // Return base64 string
  return canvas.toDataURL("image/png");
}

function filledRect(doc: jsPDF, color: string, xPos: number, yPos: number, rectWidth: number) {
  const rgb = lightenDarkenColor(color, 0);
  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
}

function unfilledRect(doc: jsPDF, xPos: number, yPos: number, rectWidth: number) {
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
}

function stripesFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
  stripedOffset: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.line(xPos, yPos + stripedOffset, xPos + rectWidth - stripedOffset, yPos + rectWidth);
  doc.line(xPos, yPos, xPos + rectWidth, yPos + rectWidth);
  doc.line(xPos + stripedOffset, yPos, xPos + rectWidth, yPos + rectWidth - stripedOffset);
}

function dashesStripesFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
  stripedOffset: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");

  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.line(xPos, yPos + stripedOffset, xPos + rectWidth - stripedOffset, yPos + rectWidth);
  doc.line(xPos, yPos, xPos + rectWidth, yPos + rectWidth);
  doc.line(xPos + stripedOffset, yPos, xPos + rectWidth, yPos + rectWidth - stripedOffset);
  doc.setDrawColor(rgb);
  doc.setLineWidth(2 * borderWidth);
  doc.line(
    xPos + rectWidth - borderWidth,
    yPos + borderWidth,
    xPos + borderWidth,
    yPos + rectWidth - borderWidth,
  );
}

function dotsFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  rectWidth: number,
  circleRadius: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setFillColor(darkRgb);
  doc.circle(xPos + rectWidth / 2, yPos + rectWidth / 2, circleRadius, "F");
}

function dotsUnfilled(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  rectWidth: number,
  circleRadius: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  doc.setFillColor(rgb);
  doc.circle(xPos + rectWidth / 2, yPos + rectWidth / 2, circleRadius, "F");
}

function unfilledRectFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");

  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(borderWidth);
  doc.rect(
    xPos + borderWidth,
    yPos + borderWidth,
    rectWidth - 2 * borderWidth,
    rectWidth - 2 * borderWidth,
    "S",
  );
}

function stripesUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
  stripedOffset: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.line(xPos, yPos + stripedOffset, xPos + rectWidth - stripedOffset, yPos + rectWidth);
  doc.line(xPos, yPos, xPos + rectWidth, yPos + rectWidth);
  doc.line(xPos + stripedOffset, yPos, xPos + rectWidth, yPos + rectWidth - stripedOffset);
}

function crossesUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
  stripedOffset: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.line(xPos, yPos + stripedOffset, xPos + rectWidth - stripedOffset, yPos + rectWidth);
  doc.line(xPos, yPos, xPos + rectWidth, yPos + rectWidth);
  doc.line(xPos + stripedOffset, yPos, xPos + rectWidth, yPos + rectWidth - stripedOffset);
  doc.line(xPos + rectWidth, yPos + stripedOffset, xPos + stripedOffset, yPos + rectWidth);
  doc.line(xPos + rectWidth, yPos, xPos, yPos + rectWidth);
  doc.line(xPos + rectWidth - stripedOffset, yPos, xPos, yPos + rectWidth - stripedOffset);
}

function linesUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.line(xPos, yPos + (1 / 3) * rectWidth, xPos + rectWidth, yPos + (1 / 3) * rectWidth);
  doc.line(xPos, yPos + (2 / 3) * rectWidth, xPos + rectWidth, yPos + (2 / 3) * rectWidth);
}

function linesFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.line(xPos, yPos + (1 / 3) * rectWidth, xPos + rectWidth, yPos + (1 / 3) * rectWidth);
  doc.line(xPos, yPos + (2 / 3) * rectWidth, xPos + rectWidth, yPos + (2 / 3) * rectWidth);
}

function verticalLinesUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.line(xPos + (1 / 4) * rectWidth, yPos, xPos + (1 / 4) * rectWidth, yPos + rectWidth);
  doc.line(xPos + (3 / 4) * rectWidth, yPos, xPos + (3 / 4) * rectWidth, yPos + rectWidth);
}

function gridUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.line(xPos + (1 / 4) * rectWidth, yPos, xPos + (1 / 4) * rectWidth, yPos + rectWidth);
  doc.line(xPos + (3 / 4) * rectWidth, yPos, xPos + (3 / 4) * rectWidth, yPos + rectWidth);
  doc.line(xPos, yPos + (1 / 4) * rectWidth, xPos + rectWidth, yPos + (1 / 4) * rectWidth);
  doc.line(xPos, yPos + (3 / 4) * rectWidth, xPos + rectWidth, yPos + (3 / 4) * rectWidth);
}

function unfilled0_9DotsUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
  circleRadius: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.circle(xPos + rectWidth / 2, yPos + rectWidth / 2, circleRadius, "S");
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, +rectWidth / 2, +rectWidth / 2, "F");
}

function triangleUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  rectWidth: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setFillColor(darkRgb);
  doc.triangle(
    xPos + (1 / 2) * rectWidth,
    yPos + (1 / 4) * rectWidth,
    xPos + (3 / 4) * rectWidth,
    yPos + (3 / 4) * rectWidth,
    xPos + (1 / 4) * rectWidth,
    yPos + (3 / 4) * rectWidth,
    "F",
  );
}

function triangleFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  rectWidth: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  const darkRgb = lightenDarkenColor(color, -30);

  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");

  doc.setFillColor(darkRgb);
  doc.triangle(
    xPos + (1 / 2) * rectWidth,
    yPos + (1 / 4) * rectWidth,
    xPos + (3 / 4) * rectWidth,
    yPos + (3 / 4) * rectWidth,
    xPos + (1 / 4) * rectWidth,
    yPos + (3 / 4) * rectWidth,
    "F",
  );
}

function ringFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
  circleRadius: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  const darkRgb = lightenDarkenColor(color, -30);

  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");

  doc.setDrawColor(darkRgb);
  doc.setLineWidth(borderWidth);
  doc.circle(xPos + rectWidth / 2, yPos + rectWidth / 2, circleRadius, "S");
}

function squareUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  rectWidth: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setFillColor(darkRgb);
  doc.rect(
    xPos + (1 / 4) * rectWidth,
    yPos + (1 / 4) * rectWidth,
    (2 / 4) * rectWidth,
    (2 / 4) * rectWidth,
    "F",
  );
}

function squareFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  rectWidth: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  const darkRgb = lightenDarkenColor(color, -30);

  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");

  doc.setFillColor(darkRgb);
  doc.rect(
    xPos + (1 / 4) * rectWidth,
    yPos + (1 / 4) * rectWidth,
    (2 / 4) * rectWidth,
    (2 / 4) * rectWidth,
    "F",
  );
}

function diamondFilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  rectWidth: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  const darkRgb = lightenDarkenColor(color, -30);

  // Background
  doc.setFillColor(rgb);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");

  // Diamond
  doc.setFillColor(darkRgb);

  const cx = xPos + rectWidth / 2;
  const cy = yPos + rectWidth / 2;
  const r = rectWidth / 4;

  doc.lines(
    [
      [r, r],    // top -> right
      [-r, r],   // right -> bottom
      [-r, -r],  // bottom -> left
    ],
    cx,
    cy - r,
    [1, 1],
    "F",
    true
  );
}

function startUnfilledRect(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  rectWidth: number,
) {
  const darkRgb = lightenDarkenColor(color, -30);
  doc.setFillColor(255, 255, 255);
  doc.rect(xPos, yPos, rectWidth, rectWidth, "F");
  doc.setFillColor(darkRgb);
  doc.triangle(
    xPos + (1 / 2) * rectWidth,
    yPos + (1 / 6) * rectWidth,
    xPos + (5 / 6) * rectWidth,
    yPos + (11 / 16) * rectWidth,
    xPos + (1 / 6) * rectWidth,
    yPos + (11 / 16) * rectWidth,
    "F",
  );
  doc.triangle(
    xPos + (1 / 2) * rectWidth,
    yPos + (5 / 6) * rectWidth,
    xPos + (5 / 6) * rectWidth,
    yPos + (5 / 16) * rectWidth,
    xPos + (1 / 6) * rectWidth,
    yPos + (5 / 16) * rectWidth,
    "F",
  );
}

// --- Line styles --- //
function line(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
  lineHeight: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  doc.setFillColor(rgb);
  doc.rect(xPos, yPos + rectWidth / 2 - lineHeight / 2, rectWidth, lineHeight, "F");
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(borderWidth);
  doc.rect(xPos, yPos + rectWidth / 2 - lineHeight / 2, rectWidth, lineHeight, "S");
}

function dashesLine(
  doc: jsPDF,
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
  lineHeight: number,
) {
  const rgb = lightenDarkenColor(color, 0);
  doc.setFillColor(rgb);
  doc.rect(
    xPos,
    yPos + rectWidth / 2 - lineHeight / 2,
    (rectWidth * 2) / 3 - borderWidth,
    lineHeight,
    "F",
  );
  doc.rect(
    xPos + (rectWidth * 2) / 3 + borderWidth,
    yPos + rectWidth / 2 - lineHeight / 2,
    (rectWidth * 1) / 3 - borderWidth,
    lineHeight,
    "F",
  );
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(borderWidth);
  doc.rect(
    xPos,
    yPos + rectWidth / 2 - lineHeight / 2,
    (rectWidth * 2) / 3 - borderWidth,
    lineHeight,
    "S",
  );
  doc.rect(
    xPos + (rectWidth * 2) / 3 + borderWidth,
    yPos + rectWidth / 2 - lineHeight / 2,
    (rectWidth * 1) / 3 - borderWidth,
    lineHeight,
    "S",
  );
}

// --- Border styles --- //
function setBorder(
  doc: jsPDF,
  type: LegendElement["type"],
  color: string,
  xPos: number,
  yPos: number,
  borderWidth: number,
  rectWidth: number,
) {
  if (
    type === "FilledRect" ||
    type === "LinesFilledRect" ||
    type === "StripesFilledRect" ||
    type === "DashesStripesFilledRect" ||
    type === "DotsFilledRect" ||
    type === "UnfilledRectFilledRect" ||
    type === "TriangleUnfilledRect" ||
    type === "SquareUnfilledRect" ||
    type === "StarUnfilledRect" ||
    type === "TriangleFilledRect" ||
    type === "SquareFilledRect" ||
    type === "RingFilledRect" || 
    type === "DiamondFilledRect"
  ) {
    doc.setDrawColor(80, 80, 80);
    doc.setLineWidth(borderWidth);
    doc.rect(xPos, yPos, rectWidth, rectWidth, "S");
  } else if (
    type === "UnfilledRect" ||
    type === "StripesUnfilledRect" ||
    type === "CrossesUnfilledRect" ||
    type === "LinesUnfilledRect" ||
    type === "0-9UnfilledDotsUnfilledRect" ||
    type === "VerticalLinesUnfilledRect" ||
    type === "GridUnfilledRect"
  ) {
    const rgb = lightenDarkenColor(color, 0);
    doc.setDrawColor(rgb);
    doc.setLineWidth(borderWidth);
    doc.rect(xPos, yPos, rectWidth, rectWidth, "S");
  }
}
